import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  Add,
  CheckCircle,
  Close,
  ArrowForward,
  AutoAwesome,
  CloudDone,
  UploadFile,
  DeleteOutline,
  EditOutlined,
  ErrorOutline,
  FactCheck,
  Inventory2,
  Key,
  Memory,
  WarningAmber,
  OpenInNew,
  PlaylistAdd,
  Refresh,
  ViewList,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import api from '../../utils/api';
import { getCategoryHeirarchy } from '../../redux/getCategoryHeirarchy';
import { getAttributesAddProdThunk } from '../../redux/productSlice';
import {
  GST_OPTIONS,
  STATUS_OPTIONS,
  applyLookupResult,
  buildCategoryOptions,
  buildOwnDescription,
  createRow,
  isSupplierHostedUrl,
  downloadQuickListTemplate,
  matchCategoryText,
  readSpreadsheetFile,
  getRowIssues,
  newSpec,
  parsePartInput,
  parsePriceBreaks,
  finalPriceWithGst,
  remapUnmappedSpecs,
  fillSpecsFromFacts,
  preferCategoryAttributes,
  suggestCategory,
} from '../../utils/quickListHelpers';
import {
  BROWSER_LOOKUP_BATCH_SIZE,
  findCatalogImage,
  getBrowserMouserKey,
  lookupPartsInBrowser,
  searchMouserBrowser,
  setBrowserMouserKey,
} from '../../utils/partLookupClient';
import { readLocalQuickProducts, saveLocalQuickProducts } from '../../utils/quickProductQueue';

// Same key as the quick product queue uses for a part.
const partKey = (row) =>
  String(row?.partNumber || row?.productNumber || '')
    .toUpperCase()
    .replace(/[\s\-_.]/g, '');

const DRAFT_KEY = 'quickListDraft:v1';
const OWN_CONTENT_KEY = 'quickList:ownContent';

const readOwnContent = () => {
  try {
    return localStorage.getItem(OWN_CONTENT_KEY) !== 'off';
  } catch (e) {
    return true;
  }
};
const LOOKUP_BATCH_SIZE = 25;
const PAGE_SIZE = 50;
const FONT = `'Public Sans', sans-serif`;
const PRIMARY = '#512DA8';
const PRIMARY_SOFT = '#F3EFFC';
const BORDER = '#E0E2E7';
const MUTED = '#667085';
const MOUSER_SIGNUP_URL = 'https://www.mouser.com/api-hub/';

const SOURCE_LABELS = { mouser: 'Mouser', digikey: 'DigiKey' };

// ------------------------------------------------------------------ draft ----

const readDraft = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
    if (!saved || !Array.isArray(saved.rows)) return null;
    // Parts already uploaded to the catalog are finished: drop them from the list.
    const uploaded = new Set(
      readLocalQuickProducts()
        .filter((item) => item.status === 'UPLOADED')
        .map((item) => partKey(item.row))
    );
    saved.rows = saved.rows
      .filter((r) => !uploaded.has(partKey(r)))
      // A lookup interrupted by a page refresh can be retried.
      .map((r) => (r.lookupStatus === 'pending' ? { ...r, lookupStatus: 'error' } : r));
    return saved;
  } catch (e) {
    return null;
  }
};

const writeDraft = (draft) => {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch (e) {
    // Storage full or blocked: the page still works, only the draft is not kept.
  }
};

// ----------------------------------------------------------------- styles ----

const cardSx = { p: 2.5, borderRadius: '10px', border: `1px solid ${BORDER}`, boxShadow: 'none' };
const fieldSx = { '& .MuiInputBase-input': { fontSize: '13px', fontFamily: FONT } };
const fieldLabelSx = { fontSize: '12px', fontWeight: 500, color: MUTED, mb: 0.5, fontFamily: FONT, whiteSpace: 'nowrap' };
const headCellSx = { fontFamily: FONT, fontWeight: 600, fontSize: '12px', color: MUTED, background: '#F9F9FC', whiteSpace: 'nowrap', py: 1.25 };
const primaryBtnSx = { textTransform: 'none', background: PRIMARY, fontFamily: FONT, fontWeight: 600, boxShadow: 'none', '&:hover': { background: PRIMARY, opacity: 0.88, boxShadow: 'none' } };
const outlineBtnSx = { textTransform: 'none', color: PRIMARY, borderColor: PRIMARY, fontFamily: FONT, fontWeight: 600 };

// The dashboard's global CSS breaks MUI floating labels (they overlap the
// value), so every field shows a plain label above the input instead.
const Field = ({ label, ...props }) => (
  <Box sx={{ minWidth: 0 }}>
    {label ? <Typography sx={fieldLabelSx}>{label}</Typography> : null}
    <TextField size="small" fullWidth {...props} sx={fieldSx} />
  </Box>
);

const SectionTitle = ({ children, sub }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontWeight: 600, fontSize: '15px', fontFamily: FONT, color: '#1D1F2C' }}>{children}</Typography>
    {sub ? <Typography sx={{ fontSize: '12.5px', color: MUTED, mt: 0.25 }}>{sub}</Typography> : null}
  </Box>
);

// The dashboard stylesheet can clip the default Material checkbox icon inside
// sticky table headers. These explicit icons keep the select-all control clear.
const SelectionCheckboxIcon = ({ state = 'empty' }) => (
  <Box
    component="span"
    sx={{
      width: 17,
      height: 17,
      borderRadius: '3px',
      boxSizing: 'border-box',
      border: `2px solid ${PRIMARY}`,
      background: state === 'empty' ? '#FFFFFF' : PRIMARY,
      color: '#FFFFFF',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: state === 'checked' ? '14px' : '16px',
      fontWeight: 800,
      lineHeight: 1,
    }}
  >
    {state === 'checked' ? '✓' : state === 'indeterminate' ? '−' : null}
  </Box>
);

const filterCategoryOptions = createFilterOptions({ limit: 100, stringify: (o) => o.label });

// ------------------------------------------------------------- row status ----

const StatusChip = ({ row }) => {
  switch (row.lookupStatus) {
    case 'pending':
      return <Chip size="small" icon={<CircularProgress size={10} />} label="Fetching" sx={{ height: 22, fontSize: '11px' }} />;
    case 'found':
      return (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={`From ${SOURCE_LABELS[row.source] || row.source}`}
          sx={{ height: 22, fontSize: '11px' }}
        />
      );
    case 'notfound':
      return <Chip size="small" color="warning" variant="outlined" label="Not found" sx={{ height: 22, fontSize: '11px' }} />;
    case 'manual':
      return <Chip size="small" variant="outlined" label="Manual" sx={{ height: 22, fontSize: '11px' }} />;
    default:
      return <Chip size="small" color="error" variant="outlined" label="Lookup failed" sx={{ height: 22, fontSize: '11px' }} />;
  }
};

// ------------------------------------------------------------- table row ----

const QuickListRow = memo(({ row, index, categoryById, onChange, onRemove, onEdit, onView, showIssues }) => {
  const rowIssues = getRowIssues(row);
  const issues = showIssues && row.include ? rowIssues : [];
  const selectedCategory = categoryById.get(row.categoryId);
  const categoryLabel = selectedCategory?.label || row.supplierCategory || 'Category not matched';
  const firstPrice = parsePriceBreaks(row.prices, row.moq)[0];
  const unitPrice = firstPrice ? `₹${firstPrice.unitPrice}` : '—';
  const salePrice = firstPrice ? `₹${finalPriceWithGst(firstPrice.unitPrice, row.gst)}` : '—';
  const ready = row.lookupStatus === 'found' && rowIssues.length === 0;
  const addedDate = row.fetchedAt ? new Date(row.fetchedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  // Rows without a description of their own get one generated from facts.
  const description = row.description || buildOwnDescription(row, categoryById);

  return (
    <TableRow hover sx={{ verticalAlign: 'top', opacity: row.include ? 1 : 0.55, background: issues.length ? '#FFF6F6' : undefined }}>
      <TableCell padding="checkbox" sx={{ pt: 1.5 }}>
        <Checkbox
          size="small"
          checked={row.include}
          onChange={(e) => onChange(row.id, { include: e.target.checked })}
          sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
        />
      </TableCell>

      {/* Product */}
      <TableCell sx={{ py: 1.5 }}>
        <Stack direction="row" spacing={1.5}>
          {row.image ? (
            <Box
              component="img"
              src={row.image}
              alt=""
              sx={{ width: 48, height: 48, flexShrink: 0, objectFit: 'contain', border: `1px solid ${BORDER}`, borderRadius: '8px', background: '#fff' }}
            />
          ) : (
            <Box sx={{ width: 48, height: 48, flexShrink: 0, borderRadius: '8px', background: PRIMARY_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Memory sx={{ color: '#B7A6E6', fontSize: 26 }} />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '13.5px', fontFamily: FONT, color: '#1D1F2C', wordBreak: 'break-all' }}>
                {index + 1}. {row.productNumber || row.partNumber}
              </Typography>
              <StatusChip row={row} />
            </Stack>
            <Typography sx={{ fontSize: '12.5px', color: row.manufacturer ? '#344054' : '#B0B4BF', mt: 0.25 }}>
              {row.manufacturer || 'No manufacturer'}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: MUTED,
                mt: 0.25,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              title={row.description ? row.description : `Auto description: ${description}`}
            >
              {description || '—'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              {row.datasheetUrl && (
                <Link href={row.datasheetUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '12px' }}>
                  Datasheet
                </Link>
              )}
              {row.supplierUrl && (
                <Link href={row.supplierUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '12px' }}>
                  Supplier page
                </Link>
              )}
              {row.existingProductId && (
                <Tooltip title="Already in the catalog. Uploading it again will UPDATE the existing product.">
                  <Link href={`/products/viewProduct/${row.existingProductId}`} target="_blank" sx={{ fontSize: '12px', color: '#0288D1' }}>
                    Already in catalog
                  </Link>
                </Tooltip>
              )}
            </Stack>
            {issues.length > 0 && <Typography sx={{ color: '#D32F2F', fontSize: '11.5px', mt: 0.5 }}>{issues.join(' · ')}</Typography>}
          </Box>
        </Stack>
      </TableCell>

      {/* Category: one level per line so the column stays narrow */}
      <TableCell sx={{ py: 1.5 }}>
        {selectedCategory ? (
          selectedCategory.names.map((name, i) => {
            const last = i === selectedCategory.names.length - 1;
            return (
              <Typography
                key={`${name}-${i}`}
                sx={{ fontSize: '12.5px', lineHeight: 1.45, fontWeight: last ? 600 : 400, color: last ? '#1D1F2C' : MUTED, wordBreak: 'break-word' }}
              >
                {name}
              </Typography>
            );
          })
        ) : (
          <Typography sx={{ fontSize: '12.5px', color: row.supplierCategory ? MUTED : '#D32F2F', wordBreak: 'break-word' }}>
            {categoryLabel}
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ py: 1.5 }}><Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{row.stock || 0}</Typography></TableCell>
      <TableCell sx={{ py: 1.5 }}><Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{unitPrice}</Typography></TableCell>
      <TableCell sx={{ py: 1.5 }}><Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{row.gst || 0}%</Typography></TableCell>
      <TableCell sx={{ py: 1.5 }}><Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{salePrice}</Typography></TableCell>
      <TableCell sx={{ py: 1.5 }}>
        <Chip
          size="small"
          color={ready ? 'success' : 'warning'}
          label={ready ? 'Ready' : 'Review'}
          sx={{
            minWidth: 72,
            fontWeight: 600,
            '& .MuiChip-label': { px: 1, overflow: 'visible', textOverflow: 'clip' },
          }}
        />
      </TableCell>
      <TableCell sx={{ py: 1.5 }}><Typography sx={{ fontSize: '12.5px', color: MUTED }}>{addedDate}</Typography></TableCell>

      {/* Actions */}
      <TableCell sx={{ whiteSpace: 'nowrap', py: 1.5, px: 0.5 }}>
        <Tooltip title="View details">
          <IconButton size="small" onClick={() => onView(row.id)} sx={{ color: '#8791A5' }}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit all details">
          <IconButton size="small" onClick={() => onEdit(row.id)} sx={{ color: PRIMARY }}>
            <EditOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove">
          <IconButton size="small" onClick={() => onRemove(row.id)}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
});

// ------------------------------------------------------ attribute editor ----

const AttributesEditor = ({ row, specs, setSpecs, attributeOptions, categoryById }) => {
  const expected = categoryById.get(row.categoryId)?.atributes || [];
  // Only our own attribute names can be chosen: the category's first, then the master list.
  const nameOptions = useMemo(
    () => preferCategoryAttributes(attributeOptions, categoryById.get(row.categoryId)),
    [attributeOptions, categoryById, row.categoryId]
  );
  // Supplier specs that never matched one of our names are not shown.
  const visibleSpecs = specs.filter((s) => s.name || !s.supplierName);
  const savedNames = specs.filter((s) => s.include && s.name.trim() && s.value.trim()).map((s) => s.name.trim().toLowerCase());
  const duplicateNames = new Set(savedNames.filter((n, i) => savedNames.indexOf(n) !== i));
  const missing = expected.filter((a) => !savedNames.includes(a.toLowerCase()) && a.toLowerCase() !== 'manufacturer');

  const update = (id, patch) => setSpecs((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const addSpec = (name = '') => setSpecs((prev) => [...prev, newSpec({ name })]);
  const addMissing = (name) => {
    const existing = specs.find((s) => s.name.toLowerCase() === name.toLowerCase());
    if (existing) update(existing.id, { include: true });
    else addSpec(name);
  };

  return (
    <Box>
      <Typography sx={{ fontSize: '12.5px', color: MUTED, mb: 1.5 }}>
        Names come from your website's attribute list; values are filled from the supplier facts or by you. Only ticked rows with a value are saved.
      </Typography>

      {expected.length > 0 && (
        <Box sx={{ mb: 1.5, p: 1.5, background: '#FAFAFC', borderRadius: '8px', border: `1px solid ${BORDER}` }}>
          <Typography sx={{ ...fieldLabelSx, mb: 1 }}>This category uses (click orange ones to add):</Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {expected.map((a) => {
              const isMissing = missing.includes(a);
              return (
                <Chip
                  key={a}
                  size="small"
                  label={a}
                  icon={isMissing ? <Add sx={{ fontSize: 14 }} /> : <CheckCircle sx={{ fontSize: 14 }} />}
                  color={isMissing ? 'warning' : 'success'}
                  variant="outlined"
                  onClick={isMissing ? () => addMissing(a) : undefined}
                  sx={{ fontSize: '11.5px' }}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      <Stack spacing={1}>
        {visibleSpecs.length === 0 && (
          <Typography sx={{ fontSize: '13px', color: MUTED, textAlign: 'center', py: 2 }}>No attributes yet.</Typography>
        )}
        {visibleSpecs.map((s) => {
          const name = s.name.trim();
          const isDuplicate = s.include && duplicateNames.has(name.toLowerCase());
          return (
            <Box key={s.id} sx={{ p: 1, borderRadius: '8px', border: `1px solid ${BORDER}`, opacity: s.include ? 1 : 0.6 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Checkbox
                  size="small"
                  checked={s.include}
                  onChange={(e) => update(s.id, { include: e.target.checked })}
                  sx={{ p: 0.75, color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Autocomplete
                    size="small"
                    options={nameOptions}
                    value={s.name || null}
                    onChange={(e, value) => update(s.id, { name: value || '', include: value ? true : s.include })}
                    renderInput={(params) => <TextField {...params} placeholder="Choose attribute" sx={fieldSx} />}
                    ListboxProps={{ style: { fontSize: '13px' } }}
                  />
                  {isDuplicate && <Typography sx={{ fontSize: '11px', color: '#D32F2F', mt: 0.25 }}>Used twice, values will be combined</Typography>}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TextField size="small" fullWidth placeholder="Value" value={s.value} onChange={(e) => update(s.id, { value: e.target.value })} sx={fieldSx} />
                </Box>
                <IconButton size="small" onClick={() => setSpecs((prev) => prev.filter((x) => x.id !== s.id))}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          );
        })}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
        <Button startIcon={<Add />} onClick={() => addSpec()} sx={{ textTransform: 'none', color: PRIMARY }}>
          Add attribute
        </Button>
        {(row.supplierFacts || row.supplierDescription) && (
          <Button
            startIcon={<AutoAwesome />}
            onClick={() => {
              const next = fillSpecsFromFacts(specs, row.supplierFacts || row.supplierDescription, attributeOptions, categoryById.get(row.categoryId));
              const added = next.filter((s) => s.include && s.value).length - specs.filter((s) => s.include && s.value).length;
              setSpecs(next);
              if (added > 0) toast.success(`${added} attribute value(s) filled from supplier facts`);
              else toast.info('No more values found in the supplier facts for this part');
            }}
            sx={{ textTransform: 'none', color: PRIMARY }}
          >
            Fill from supplier facts
          </Button>
        )}
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------- detail drawer ----

export const RowEditor = ({ row, focus, attributeOptions, categoryOptions, categoryById, onClose, onSave }) => {
  const [draft, setDraft] = useState(() => ({ ...row }));
  const [specs, setSpecs] = useState(() => (row.specs || []).map((s) => ({ ...s })));
  const set = (field) => (e) => setDraft((d) => ({ ...d, [field]: e.target.value }));

  useEffect(() => {
    if (focus === 'attributes') {
      document.getElementById('ql-attributes')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [focus]);

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 600 } } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '17px', fontFamily: FONT }}>{draft.productNumber || draft.partNumber}</Typography>
          <Typography sx={{ fontSize: '12.5px', color: MUTED }}>{draft.manufacturer || 'Edit product details'}</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        <SectionTitle>Basic details</SectionTitle>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          {draft.image ? (
            <Box component="img" src={draft.image} alt="" sx={{ width: 88, height: 88, objectFit: 'contain', border: `1px solid ${BORDER}`, borderRadius: '10px', flexShrink: 0 }} />
          ) : (
            <Box sx={{ width: 88, height: 88, border: `1px dashed ${BORDER}`, borderRadius: '10px', flexShrink: 0, background: '#FAFAFC' }} />
          )}
          <Box sx={{ flex: 1, display: 'grid', gap: 1.5 }}>
            <Field label="Part number" value={draft.productNumber} onChange={set('productNumber')} />
            <Field label="Manufacturer" value={draft.manufacturer} onChange={set('manufacturer')} />
          </Box>
        </Stack>
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Box>
            <Field
              label="Description"
              value={draft.description}
              onChange={set('description')}
              multiline
              minRows={2}
              maxRows={5}
              placeholder={buildOwnDescription({ ...draft, specs }, categoryById)}
            />
            {!draft.description && (
              <Typography sx={{ fontSize: '11px', color: MUTED, mt: 0.5 }}>
                Empty = our own description is generated from the facts (shown in grey). Type to write your own.
              </Typography>
            )}
          </Box>
          <Field
            label="Datasheet URL"
            value={draft.datasheetUrl}
            onChange={set('datasheetUrl')}
            InputProps={{
              endAdornment: draft.datasheetUrl ? (
                <IconButton size="small" href={draft.datasheetUrl} target="_blank" rel="noopener noreferrer">
                  <OpenInNew sx={{ fontSize: 16 }} />
                </IconButton>
              ) : null,
            }}
          />
          <Box>
            <Field label="Image URL" value={draft.image} onChange={(e) => setDraft((d) => ({ ...d, image: e.target.value, imageAuto: false }))} />
            {draft.imageAuto && draft.image && (
              <Typography sx={{ fontSize: '11px', color: MUTED, mt: 0.5 }}>From a product in the same category on our website.</Typography>
            )}
            {isSupplierHostedUrl(draft.image) && (
              <Typography sx={{ fontSize: '11px', color: '#ED6C02', mt: 0.5 }}>This image is hosted by the supplier — replace it with our own.</Typography>
            )}
          </Box>
          <Box>
            <Typography sx={fieldLabelSx}>Category</Typography>
            <Autocomplete
              size="small"
              options={categoryOptions}
              value={categoryById.get(draft.categoryId) || null}
              filterOptions={filterCategoryOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(e, option) => {
                setDraft((d) => ({
                  ...d,
                  categoryId: option?.id || '',
                  categoryAuto: false,
                  // A catalog image follows the category; pick it again for the new one.
                  ...(d.imageAuto || !d.image ? { image: '', imageAuto: true, imageCategoryId: '' } : {}),
                }));
                // Fill the new category's attributes from the supplier facts.
                if (option) setSpecs((prev) => fillSpecsFromFacts(prev, draft.supplierFacts || draft.supplierDescription, attributeOptions, option));
              }}
              renderInput={(params) => <TextField {...params} placeholder="Select category" sx={fieldSx} />}
              ListboxProps={{ style: { fontSize: '13px' } }}
            />
            {draft.supplierCategoryPath?.length > 0 && (
              <Typography sx={{ fontSize: '11px', color: MUTED, mt: 0.5 }}>Supplier: {draft.supplierCategoryPath.join(' > ')}</Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />
        <SectionTitle sub='Price: one value (12.5) or breaks as qty:price (1:12.5, 100:10). Excluding GST.'>Stock & pricing</SectionTitle>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
          <Field label="Stock" type="number" value={draft.stock} onChange={set('stock')} />
          <Field label="MOQ" type="number" value={draft.moq} onChange={set('moq')} />
          <Field label="GST %" select value={draft.gst} onChange={set('gst')}>
            {GST_OPTIONS.map((g) => (
              <MenuItem key={g} value={g}>{g}%</MenuItem>
            ))}
          </Field>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Field label="Unit price (₹)" placeholder="12.5 or 1:12.5, 100:10" value={draft.prices} onChange={set('prices')} />
          </Box>
          <Box>
            <Field label="HSN code" value={draft.hsn} onChange={set('hsn')} />
            {draft.hsCodeHint && !draft.hsn && (
              <Link component="button" onClick={() => setDraft((d) => ({ ...d, hsn: d.hsCodeHint }))} sx={{ fontSize: '11px', mt: 0.5 }}>
                Supplier HS code: {draft.hsCodeHint} (use)
              </Link>
            )}
          </Box>
          <Field label="SKU" value={draft.sku} onChange={set('sku')} />
          <Field label="Standard pack qty" value={draft.spq || ''} onChange={set('spq')} />
          <Field label="Lead time" value={draft.leadTime} onChange={set('leadTime')} />
          <Field label="Status" select value={draft.status} onChange={set('status')}>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Field>
          <Field label="RoHS" select value={draft.rohs} onChange={set('rohs')}>
            <MenuItem value="YES">YES</MenuItem>
            <MenuItem value="NO">NO</MenuItem>
          </Field>
        </Box>

        <Divider sx={{ my: 3 }} />
        <Box id="ql-attributes">
          <SectionTitle>Attributes</SectionTitle>
          <AttributesEditor
            row={draft}
            specs={specs}
            setSpecs={setSpecs}
            attributeOptions={attributeOptions}
            categoryById={categoryById}
          />
        </Box>
      </Box>

      <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ px: 3, py: 2, borderTop: `1px solid ${BORDER}` }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: MUTED }}>Cancel</Button>
        <Button
          variant="contained"
          // Specs without one of our attribute names are dropped on save.
          onClick={() => onSave(row.id, { ...draft, specs: specs.filter((s) => s.name || !s.supplierName) })}
          sx={primaryBtnSx}
        >
          Save changes
        </Button>
      </Stack>
    </Drawer>
  );
};

// -------------------------------------------------------- connect dialog ----

const ConnectDialog = ({ providers, browserMode, browserKey, onClose, onSaved, onBrowserKeySaved }) => {
  const [mouserKey, setMouserKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [showDigikey, setShowDigikey] = useState(false);
  const [digikeyId, setDigikeyId] = useState('');
  const [digikeySecret, setDigikeySecret] = useState('');
  const [testPart, setTestPart] = useState('PI3CH480QEX');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const mouserFromEnv = providers?.source?.mouserApiKey === 'env';

  const runTest = async (keyOverride) => {
    const part = testPart.trim() || 'PI3CH480QEX';
    setTesting(true);
    setTestResult(null);
    try {
      if (browserMode) {
        const found = await searchMouserBrowser([part], keyOverride || browserKey);
        setTestResult({ ok: true, result: [...found.values()][0] || { found: false } });
      } else {
        const res = await api.quickListTest(part);
        setTestResult({ ok: true, result: res?.data?.data?.result });
      }
    } catch (err) {
      setTestResult({ ok: false, message: err?.response?.data?.message || err?.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (browserMode) {
      const key = mouserKey.trim();
      if (!key) {
        toast.error('Paste your Mouser Search API key first');
        return;
      }
      setBrowserMouserKey(key);
      onBrowserKeySaved(key);
      setMouserKey('');
      toast.success('Saved in this browser. Testing the connection…');
      await runTest(key);
      return;
    }

    const payload = {};
    if (mouserKey.trim()) payload.mouserApiKey = mouserKey.trim();
    if (digikeyId.trim()) payload.digikeyClientId = digikeyId.trim();
    if (digikeySecret.trim()) payload.digikeyClientSecret = digikeySecret.trim();
    if (!Object.keys(payload).length) {
      toast.error('Paste your Mouser API key first');
      return;
    }
    setSaving(true);
    try {
      const res = await api.quickListSaveSettings(payload);
      onSaved(res?.data?.data);
      toast.success('Saved. Testing the connection…');
      setMouserKey('');
      setDigikeyId('');
      setDigikeySecret('');
      await runTest();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const connected = browserMode ? Boolean(browserKey) : Boolean(providers && (providers.mouser || providers.digikey));
  const browserKeyHint = browserKey ? `••••${browserKey.slice(-4)}` : '';

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: FONT, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Key sx={{ color: PRIMARY }} /> Connect part data source
      </DialogTitle>
      <DialogContent dividers>
        {browserMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This server does not have the Quick List API yet, so the dashboard calls Mouser <b>directly from this browser</b>. The key is
            saved in this browser only.
          </Alert>
        )}
        {connected ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Connected:{' '}
            {browserMode
              ? `Mouser (${browserKeyHint})`
              : [providers.mouser && `Mouser (${providers.mouserKeyHint})`, providers.digikey && 'DigiKey'].filter(Boolean).join(' + ')}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Quick List fetches description, manufacturer, datasheet, image, category and specs from Mouser. Mouser needs a free API key.
          </Alert>
        )}

        <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 1 }}>Get a free Mouser key (about 5 minutes)</Typography>
        <Box component="ol" sx={{ pl: 2.5, mt: 0, mb: 2, fontSize: '13px', color: '#344054', '& li': { mb: 0.5 } }}>
          <li>
            Open{' '}
            <Link href={MOUSER_SIGNUP_URL} target="_blank" rel="noopener noreferrer">
              mouser.com/api-hub <OpenInNew sx={{ fontSize: 12, verticalAlign: 'middle' }} />
            </Link>{' '}
            and sign in (or create a free Mouser account).
          </li>
          <li>Under <b>Search API</b>, click <b>Sign up</b> and submit the short form.</li>
          <li>
            Copy the <b>Search API</b> key (My Account → APIs) and paste it below. The key labelled for Order/Cart API will not work.
          </li>
        </Box>

        <Typography sx={fieldLabelSx}>Mouser Search API key</Typography>
        <TextField
          size="small"
          fullWidth
          type={showKey ? 'text' : 'password'}
          placeholder={
            browserMode
              ? browserKey
                ? `Saved (${browserKeyHint}) — paste to replace`
                : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
              : mouserFromEnv
              ? 'Set in the server .env file'
              : providers?.mouser
              ? `Saved (${providers.mouserKeyHint}) — paste to replace`
              : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
          }
          value={mouserKey}
          disabled={!browserMode && mouserFromEnv}
          onChange={(e) => setMouserKey(e.target.value)}
          sx={fieldSx}
          InputProps={{
            endAdornment: (
              <IconButton size="small" onClick={() => setShowKey((v) => !v)}>
                {showKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            ),
          }}
        />

        {!browserMode && (
          <Button size="small" onClick={() => setShowDigikey((v) => !v)} sx={{ mt: 1, textTransform: 'none', color: MUTED }}>
            {showDigikey ? 'Hide' : 'Also add'} DigiKey (optional, used when Mouser does not have a part)
          </Button>
        )}
        {!browserMode && showDigikey && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
            <Field label="DigiKey Client ID" value={digikeyId} onChange={(e) => setDigikeyId(e.target.value)} placeholder={providers?.digikey ? 'Saved — paste to replace' : ''} />
            <Field label="DigiKey Client Secret" type="password" value={digikeySecret} onChange={(e) => setDigikeySecret(e.target.value)} />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 1 }}>Test the connection</Typography>
        <Stack direction="row" spacing={1}>
          <TextField size="small" value={testPart} onChange={(e) => setTestPart(e.target.value)} sx={{ ...fieldSx, flex: 1 }} placeholder="Part number" />
          <Button variant="outlined" onClick={() => runTest()} disabled={testing || !connected} sx={outlineBtnSx}>
            {testing ? 'Testing…' : 'Test'}
          </Button>
        </Stack>
        {testResult?.ok && testResult.result?.found && (
          <Alert severity="success" sx={{ mt: 1.5 }}>
            Working! <b>{testResult.result.productNumber}</b> · {testResult.result.manufacturer} · {Object.keys(testResult.result.attributes || {}).length} specs from{' '}
            {SOURCE_LABELS[testResult.result.source] || testResult.result.source}
          </Alert>
        )}
        {testResult?.ok && !testResult.result?.found && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>The key works, but this part number was not found. Try another part.</Alert>
        )}
        {testResult && !testResult.ok && <Alert severity="error" sx={{ mt: 1.5 }}>{testResult.message}</Alert>}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: MUTED }}>Close</Button>
        <Button variant="contained" onClick={save} disabled={saving} sx={primaryBtnSx}>
          {saving ? 'Saving…' : 'Save & test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ------------------------------------------------------------ page parts ----

const StepBar = ({ steps }) => {
  const activeIndex = Math.max(0, steps.findIndex((s) => !s.done));
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ mb: 2.5, px: { xs: 1.25, sm: 1.75 }, py: 1.25, flexWrap: 'wrap', gap: 1, border: `1px solid ${BORDER}`, borderRadius: '12px', background: '#fff', boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)' }}
    >
      {steps.map((step, i) => {
        const state = step.done ? 'done' : i === activeIndex ? 'active' : 'todo';
        return (
          <React.Fragment key={step.label}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: state === 'todo' ? MUTED : '#fff',
                  background: state === 'done' ? '#12B76A' : state === 'active' ? PRIMARY : '#EAECF0',
                }}
              >
                {state === 'done' ? <CheckCircle sx={{ fontSize: 16 }} /> : i + 1}
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: state === 'active' ? 600 : 500, color: state === 'todo' ? MUTED : '#1D1F2C' }}>
                {step.label}
              </Typography>
            </Stack>
            {i < steps.length - 1 && <Box sx={{ width: 40, height: 2, borderRadius: 1, background: step.done ? '#12B76A' : '#EAECF0', mx: 1 }} />}
          </React.Fragment>
        );
      })}
    </Stack>
  );
};

const BANNER_TONES = {
  error: { bg: '#FEF3F2', border: '#FECDCA', icon: <ErrorOutline sx={{ color: '#D92D20' }} /> },
  warning: { bg: '#FFFAEB', border: '#FEDF89', icon: <WarningAmber sx={{ color: '#DC6803' }} /> },
};

const Banner = ({ tone, action, children }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ mb: 2, px: 2, py: 1.25, borderRadius: '10px', background: BANNER_TONES[tone].bg, border: `1px solid ${BANNER_TONES[tone].border}` }}
  >
    {BANNER_TONES[tone].icon}
    <Typography sx={{ fontSize: '13px', color: '#344054', flex: 1 }}>{children}</Typography>
    {action}
  </Stack>
);

const STAT_TONES = {
  neutral: { bg: '#F9FAFB', color: '#475467' },
  success: { bg: '#ECFDF3', color: '#039855' },
  warning: { bg: '#FFFAEB', color: '#DC6803' },
  primary: { bg: PRIMARY_SOFT, color: PRIMARY },
};

const StatTile = ({ icon, label, value, hint, tone }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5, borderRadius: '10px', border: `1px solid ${BORDER}`, background: '#fff' }}>
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: '10px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: STAT_TONES[tone].bg,
        color: STAT_TONES[tone].color,
        '& svg': { fontSize: 20 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1D1F2C', lineHeight: 1.1 }}>{value}</Typography>
      <Typography sx={{ fontSize: '12px', color: MUTED }} noWrap title={hint || label}>
        {label}
      </Typography>
    </Box>
  </Stack>
);

// ------------------------------------------------------------------- page ----

const QuickList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categoryHeirarchy } = useSelector((state) => state.categoryHeirarchy);
  const attributeList = useSelector((state) => state.products?.atributes?.attributeOptions);
  const attributeOptions = useMemo(
    () => [...new Set((attributeList || []).map((a) => String(a || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [attributeList]
  );

  const draft = useMemo(readDraft, []);
  const [input, setInput] = useState('');
  const [rows, setRows] = useState(draft?.rows || []);
  const [fetching, setFetching] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [warnings, setWarnings] = useState([]);
  const [providers, setProviders] = useState(null); // null = loading, false = API not reachable
  const [showIssues, setShowIssues] = useState(false);
  const [editing, setEditing] = useState(null); // { id, focus }
  const [viewingRowId, setViewingRowId] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [browserKey, setBrowserKey] = useState(getBrowserMouserKey);
  const [page, setPage] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [ownContent, setOwnContent] = useState(readOwnContent);
  const [confirmAction, setConfirmAction] = useState(null);
  const fileInputRef = useRef(null);
  const catalogImageCache = useRef(new Map()); // categoryId -> image URL ('' = none)

  const toggleOwnContent = (on) => {
    setOwnContent(on);
    try {
      localStorage.setItem(OWN_CONTENT_KEY, on ? 'on' : 'off');
    } catch (e) {
      // Not remembered; the switch still works for this visit.
    }
  };

  const categoryOptions = useMemo(() => buildCategoryOptions(categoryHeirarchy), [categoryHeirarchy]);
  const categoryById = useMemo(() => new Map(categoryOptions.map((o) => [o.id, o])), [categoryOptions]);

  // Without the server API (e.g. on api.electrowiz.in before deploy) the
  // dashboard looks parts up directly from the browser.
  const browserMode = providers === false;
  const connected = browserMode ? Boolean(browserKey) : Boolean(providers && (providers.mouser || providers.digikey));

  const loadProviders = useCallback(() => {
    api
      .quickListProviders()
      .then((res) => setProviders(res?.data?.data || { mouser: false, digikey: false }))
      .catch(() => setProviders(false));
  }, []);

  useEffect(() => {
    dispatch(getCategoryHeirarchy());
    dispatch(getAttributesAddProdThunk());
    loadProviders();
  }, [dispatch, loadProviders]);

  useEffect(() => {
    writeDraft({ rows });
  }, [rows]);

  // Attribute values read from the supplier facts follow the category: when a
  // fetched row gets (or changes) its category, its attributes are filled in.
  useEffect(() => {
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        const facts = r.supplierFacts || r.supplierDescription;
        if (r.lookupStatus !== 'found' || !facts) return r;
        // Wait until our attribute names are known (category list or master list).
        if (r.categoryId ? !categoryById.has(r.categoryId) : !attributeOptions.length) return r;
        const fillKey = `${r.categoryId || '-'}:${attributeOptions.length}`;
        if (r.factsFilledFor === fillKey) return r;
        changed = true;
        return {
          ...r,
          factsFilledFor: fillKey,
          specs: fillSpecsFromFacts(r.specs || [], facts, attributeOptions, categoryById.get(r.categoryId)),
        };
      });
      return changed ? next : prev;
    });
  }, [rows, attributeOptions, categoryById]);

  // Categories can arrive after parts were fetched (or from a saved draft):
  // suggest one for every found row that has none yet.
  useEffect(() => {
    if (!categoryOptions.length) return;
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        if (!r.categoryId && r.categoryText) {
          const fileCategory = matchCategoryText(r.categoryText, categoryOptions);
          if (fileCategory) {
            changed = true;
            return { ...r, categoryId: fileCategory.id };
          }
        }
        if (r.categoryId || r.lookupStatus !== 'found' || !r.supplierCategory) return r;
        const suggestion = suggestCategory(r.supplierCategoryPath, r.supplierCategory, categoryOptions);
        if (!suggestion) return r;
        changed = true;
        return { ...r, categoryId: suggestion.id, categoryAuto: true };
      });
      return changed ? next : prev;
    });
  }, [categoryOptions]);

  // Same for attributes: map supplier specs once our attribute list has loaded.
  useEffect(() => {
    if (!attributeOptions.length) return;
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        if (!Array.isArray(r.specs) || !r.specs.length) return r;
        const specs = remapUnmappedSpecs(r.specs, attributeOptions);
        if (specs === r.specs) return r;
        changed = true;
        return { ...r, specs };
      });
      return changed ? next : prev;
    });
  }, [attributeOptions]);

  // Our own image: rows with a category and no image of their own get the
  // image of a product in the same category on our website.
  useEffect(() => {
    const wantsCatalogImage = (r) =>
      ownContent && r.categoryId && (!r.image || r.imageAuto) && r.imageCategoryId !== r.categoryId;
    const needed = [...new Set(rows.filter(wantsCatalogImage).map((r) => r.categoryId))];
    if (!needed.length) return undefined;

    let cancelled = false;
    (async () => {
      for (const categoryId of needed) {
        if (!catalogImageCache.current.has(categoryId)) {
          catalogImageCache.current.set(categoryId, await findCatalogImage(categoryId));
        }
      }
      if (cancelled) return;
      setRows((prev) =>
        prev.map((r) =>
          wantsCatalogImage(r) && catalogImageCache.current.has(r.categoryId)
            ? { ...r, image: catalogImageCache.current.get(r.categoryId), imageAuto: true, imageCategoryId: r.categoryId }
            : r
        )
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [rows, ownContent]);

  // Clears supplier-written content from rows fetched earlier (or with the
  // switch off): description, supplier-hosted image/datasheet and lead time.
  const removeSupplierContent = () => {
    const affected = rows.filter((r) => r.lookupStatus === 'found');
    if (!affected.length) {
      toast.info('No fetched rows in the list');
      return;
    }
    setConfirmAction({
      title: 'Remove supplier content?',
      description: `This removes the supplier description, supplier images, datasheet links and lead time from ${affected.length} fetched row(s). Any description typed in these rows will also be cleared.`,
      confirmLabel: 'Remove content',
      onConfirm: () => {
        setRows((prev) =>
          prev.map((r) => {
            if (r.lookupStatus !== 'found') return r;
            return {
              ...r,
              description: '',
              leadTime: '',
              datasheetUrl: isSupplierHostedUrl(r.datasheetUrl) ? '' : r.datasheetUrl,
              ...(isSupplierHostedUrl(r.image) ? { image: '', imageAuto: true, imageCategoryId: '' } : {}),
            };
          })
        );
        toggleOwnContent(true);
        toast.success('Supplier content removed. Our own description and catalog images are used now.');
      },
    });
  };

  const updateRow = useCallback((id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const removeRow = useCallback((id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const openEditor = useCallback((id, focus) => setEditing({ id, focus }), []);
  const openPreview = useCallback((id) => setViewingRowId(id), []);

  const saveEditor = (id, next) => {
    updateRow(id, next);
    setEditing(null);
  };

  const lookupRows = async (rowsToLookup) => {
    if (!rowsToLookup.length) return;
    setFetching(true);
    setWarnings([]);
    setProgress({ done: 0, total: rowsToLookup.length });
    const collectedWarnings = new Set();
    const batchSize = browserMode ? BROWSER_LOOKUP_BATCH_SIZE : LOOKUP_BATCH_SIZE;
    const rowIds = new Set(rowsToLookup.map((r) => r.id));
    setRows((prev) => prev.map((r) => (rowIds.has(r.id) ? { ...r, lookupStatus: 'pending' } : r)));

    for (let i = 0; i < rowsToLookup.length; i += batchSize) {
      const batch = rowsToLookup.slice(i, i + batchSize);
      const batchIds = new Set(batch.map((r) => r.id));

      try {
        const partNumbers = batch.map((r) => r.partNumber.trim());
        let payload;
        if (browserMode) {
          payload = await lookupPartsInBrowser(partNumbers, browserKey);
          if (!payload.results) throw new Error(payload.warnings[0] || 'Lookup failed');
        } else {
          const res = await api.quickListLookup(partNumbers);
          payload = res?.data?.data || {};
        }
        (payload.warnings || []).forEach((w) => collectedWarnings.add(w));
        const byPart = new Map((payload.results || []).map((result) => [String(result.partNumber).trim(), result]));

        const fetchedAt = new Date().toISOString();
        const fetchedRows = batch.map((row) => ({
          ...applyLookupResult(row, byPart.get(row.partNumber.trim()) || { found: false }, categoryOptions, attributeOptions, { ownContent }),
          fetchedAt,
        }));
        const fetchedById = new Map(fetchedRows.map((row) => [row.id, row]));
        setRows((prev) => prev.map((row) => fetchedById.get(row.id) || row));

      } catch (err) {
        const message =
          err?.response?.status === 404
            ? 'The connected server does not have the Quick List API. Restart the backend with the latest code.'
            : err?.response?.data?.message || err?.message || 'Lookup failed';
        collectedWarnings.add(message);
        setRows((prev) => prev.map((r) => (batchIds.has(r.id) ? { ...r, lookupStatus: 'error' } : r)));
      }

      setProgress((p) => ({ ...p, done: Math.min(p.done + batch.length, p.total) }));
    }

    setWarnings([...collectedWarnings]);
    setFetching(false);
  };

  const handleFetch = async () => {
    const entries = parsePartInput(input);
    if (!entries.length) {
      toast.error('Paste at least one part number');
      return;
    }
    const added = await addEntries(entries);
    if (added) setInput('');
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      toast.error('Please choose an Excel (.xlsx, .xls) or CSV file');
      return;
    }
    try {
      const entries = await readSpreadsheetFile(file);
      if (!entries.length) {
        toast.error('No part numbers found in the file. Put them in the first column or under a "Part Number" header.');
        return;
      }
      toast.success(`${entries.length} part number(s) read from ${file.name}`);
      await addEntries(entries);
    } catch (e) {
      toast.error('Could not read this file');
    }
  };

  // Adds entries as rows (skipping ones already listed) and fetches their details.
  const addEntries = async (entries) => {
    const existing = new Set(rows.map((r) => r.partNumber.trim().toUpperCase()));
    const fresh = entries.filter((e) => !existing.has(e.partNumber.toUpperCase()));
    const skipped = entries.length - fresh.length;
    if (skipped > 0) toast.info(`${skipped} part number(s) already in the list were skipped`);
    if (!fresh.length) return skipped > 0;

    const newRows = fresh.map((entry) => {
      const row = createRow(entry);
      const fileCategory = matchCategoryText(row.categoryText, categoryOptions);
      return {
        ...row,
        ...(fileCategory ? { categoryId: fileCategory.id } : {}),
        ...(connected ? {} : { lookupStatus: 'manual' }),
      };
    });
    setRows((prev) => [...prev, ...newRows]);
    // Show the page where the new rows start.
    setPage(Math.floor(rows.length / PAGE_SIZE));

    if (!connected) {
      toast.info('Connect Mouser to fetch the details automatically');
      setConnectOpen(true);
      return true;
    }
    await lookupRows(newRows);
    return true;
  };

  const refetchable = rows.filter((r) => ['manual', 'error'].includes(r.lookupStatus));

  // Rows fetched before supplier facts were kept cannot fill their attributes.
  // Look them up once more (values typed by the user are kept).
  const factsRefetchDone = useRef(false);
  useEffect(() => {
    if (factsRefetchDone.current || !connected || fetching) return;
    const missingFacts = rows.filter((r) => r.lookupStatus === 'found' && !r.supplierFacts && !r.supplierDescription);
    factsRefetchDone.current = true;
    if (missingFacts.length) {
      toast.info(`Fetching attributes for ${missingFacts.length} earlier row(s)…`);
      lookupRows(missingFacts);
    }
    // lookupRows is recreated every render; this runs once when the page is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, fetching, rows]);

  const clearAll = () => {
    if (!rows.length) return;
    setConfirmAction({
      title: 'Clear Quick List?',
      description: `This will permanently remove all ${rows.length} row${rows.length === 1 ? '' : 's'} from this Quick List. You cannot undo this action.`,
      confirmLabel: 'Clear list',
      onConfirm: () => {
        setRows([]);
        setWarnings([]);
        setShowIssues(false);
      },
    });
  };

  const selectedRows = rows.filter((r) => r.include);
  const selectedInvalidCount = selectedRows.filter((r) => getRowIssues(r).length > 0).length;
  const canMoveSelectedRows = selectedRows.length > 0 && selectedInvalidCount === 0 && !fetching;

  const summary = useMemo(
    () => ({
      total: rows.length,
      found: rows.filter((r) => r.lookupStatus === 'found').length,
      notFound: rows.filter((r) => r.lookupStatus === 'notfound').length,
      failed: rows.filter((r) => r.lookupStatus === 'error').length,
      existing: rows.filter((r) => r.existingProductId).length,
      ready: rows.filter((r) => r.include && getRowIssues(r).length === 0).length,
    }),
    [rows]
  );

  // Step 3: the selected rows go to the All Quick Products page, where the
  // final Download Excel / Send to Bulk Upload happens.
  const handleNext = () => {
    if (!selectedRows.length) {
      toast.error('Select at least one row');
      return;
    }
    const invalid = selectedRows.filter((r) => getRowIssues(r).length > 0);
    if (invalid.length) {
      setShowIssues(true);
      toast.error(`${invalid.length} row(s) need attention (shown in red). Fix them with the edit button first.`);
      return;
    }

    const items = selectedRows.map((row) => ({
      row,
      categoryLabel: categoryById.get(row.categoryId)?.label || row.supplierCategory || 'Uncategorised',
    }));
    saveLocalQuickProducts(items);
    api.saveQuickProducts(items).catch(() => {});
    const movedRowIds = new Set(selectedRows.map((row) => row.id));
    const remainingRows = rows.filter((row) => !movedRowIds.has(row.id));
    // Persist before navigating. React may unmount this page before the
    // rows-effect runs, which used to make moved rows appear again on return.
    writeDraft({ rows: remainingRows });
    setRows(remainingRows);
    setPage(0);
    toast.success(`${items.length} product(s) moved to All Quick Products`);
    navigate('/products/allQuickProducts');
  };

  const editingRow = editing && rows.find((r) => r.id === editing.id);
  const viewingRow = rows.find((r) => r.id === viewingRowId);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const parsedInput = parsePartInput(input);
  const inputCount = parsedInput.length;

  return (
  <Box sx={{ fontFamily: FONT, pb: 10, px: { xs: 1.5, md: 2.5 } }}>
      {/* Header */}
      <Paper sx={{ mb: 2.5, px: { xs: 1.75, sm: 2.5 }, py: 2.25, borderRadius: '14px', border: `1px solid ${BORDER}`, boxShadow: '0 4px 14px rgba(16, 24, 40, 0.05)', background: 'linear-gradient(110deg, #FFFFFF 0%, #FBFAFF 100%)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
          <Stack spacing={0.75}>
            <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}>
              <Typography color="text.secondary" sx={{ fontSize: '13px', fontFamily: FONT }}>Dashboard</Typography>
              <Link component="button" underline="hover" color="text.secondary" sx={{ fontSize: '13px', fontFamily: FONT }} onClick={() => navigate('/products/allProduct')}>
                Product
              </Link>
              <Typography color="text.primary" sx={{ fontSize: '13px', fontFamily: FONT }}>Quick List</Typography>
            </Breadcrumbs>
            <Typography sx={{ color: '#1D1F2C', fontSize: { xs: '26px', sm: '30px' }, fontWeight: 700, fontFamily: FONT, letterSpacing: '-0.03em', lineHeight: 1.15 }}>Quick List</Typography>
            <Typography sx={{ color: MUTED, fontSize: '14px', fontFamily: FONT, maxWidth: 620 }}>
              Add products in bulk, enrich their details, then review everything in one place.
            </Typography>
          </Stack>
          <Button variant="outlined" startIcon={<ViewList />} onClick={() => navigate('/products/allQuickProducts')} sx={{ ...outlineBtnSx, borderRadius: '8px', px: 1.75, whiteSpace: 'nowrap' }}>
            All Quick Products
          </Button>
        </Stack>
      </Paper>

      <StepBar
        steps={[
          { label: 'Paste part numbers', done: rows.length > 0 },
          { label: 'Review & fill price', done: rows.length > 0 && summary.ready === rows.length },
          { label: 'Upload or download', done: false },
        ]}
      />

      {providers !== null && !connected && (
        <Banner tone="warning" action={<Button size="small" variant="contained" startIcon={<Key />} onClick={() => setConnectOpen(true)} sx={primaryBtnSx}>Connect Mouser</Button>}>
          <b>One-time setup:</b> connect your free Mouser Search API key so descriptions, images, datasheets and attributes are filled
          automatically.
        </Banner>
      )}

      {/* Add part numbers */}
      <Paper sx={{ ...cardSx, p: { xs: 1.75, sm: 2.5 }, mb: 2, borderRadius: '14px', boxShadow: '0 4px 14px rgba(16, 24, 40, 0.04)' }}>
        <Box>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.75 }}>
              <Box sx={{ display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: '10px', background: PRIMARY_SOFT, color: PRIMARY }}><PlaylistAdd fontSize="small" /></Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '16px', fontFamily: FONT, color: '#1D1F2C', lineHeight: 1.3 }}>Add part numbers</Typography>
                <Typography sx={{ fontSize: '12.5px', color: MUTED, mt: 0.25, fontFamily: FONT }}>Upload an Excel/CSV file with part numbers, or paste them below. Details are fetched for every part.</Typography>
              </Box>
            </Stack>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                handleFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (!fetching) handleFile(e.dataTransfer.files?.[0]);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                p: { xs: 1.5, sm: 2.25 },
                mb: 2,
                borderRadius: '12px',
                border: `1.5px dashed ${dragOver ? PRIMARY : '#CFC2F4'}`,
                background: dragOver ? '#EDE7FB' : '#FBFAFF',
                transition: 'all .15s',
                boxShadow: dragOver ? '0 0 0 3px rgba(81, 45, 168, 0.10)' : 'none',
              }}
            >
              <Box sx={{ width: 44, height: 44, borderRadius: '10px', background: PRIMARY_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <UploadFile sx={{ color: PRIMARY }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1D1F2C' }}>
                  {dragOver ? 'Drop the file to add its parts' : 'Upload Excel / CSV'}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: MUTED }}>
                  Drag & drop here. Use the Quick Create Format — only <b>productNumber</b> is required, anything else you fill in is kept.{' '}
                  <Link component="button" onClick={downloadQuickListTemplate} sx={{ fontSize: '12px', verticalAlign: 'baseline' }}>
                    Download Quick Create Format
                  </Link>
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<UploadFile />} onClick={() => fileInputRef.current?.click()} disabled={fetching} sx={primaryBtnSx}>
                Choose file
              </Button>
            </Box>

            <Divider sx={{ mb: 1.5, '&::before, &::after': { borderColor: BORDER } }}>
              <Typography sx={{ fontSize: '12px', color: MUTED }}>or paste part numbers (one per line, optional stock and price)</Typography>
            </Divider>
            <TextField
              multiline
              minRows={4}
              maxRows={12}
              fullWidth
              placeholder={'PI3CH480QEX\nPI5C3303TEX, 10000\nMCP6561UT-E/OT, 500, 8.75'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={fetching}
              sx={{
                '& .MuiOutlinedInput-root': { background: '#FFFFFF', borderRadius: '12px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' },
                '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: PRIMARY },
                '& textarea': { fontFamily: 'Consolas, monospace', fontSize: '13px', lineHeight: 1.8, padding: '14px 16px' },
              }}
            />
            {parsedInput.length > 0 && (
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                {parsedInput.slice(0, 12).map((e) => (
                  <Chip
                    key={e.partNumber}
                    size="small"
                    label={e.partNumber}
                    sx={{ background: PRIMARY_SOFT, color: PRIMARY, fontWeight: 500, fontSize: '12px', fontFamily: 'Consolas, monospace' }}
                  />
                ))}
                {parsedInput.length > 12 && <Chip size="small" label={`+${parsedInput.length - 12} more`} />}
              </Stack>
            )}
            <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button variant="contained" startIcon={<PlaylistAdd />} onClick={handleFetch} disabled={fetching || !inputCount} sx={primaryBtnSx}>
                {fetching ? 'Fetching…' : connected ? `Fetch details${inputCount ? ` (${inputCount})` : ''}` : `Add to list${inputCount ? ` (${inputCount})` : ''}`}
              </Button>
              {connected && refetchable.length > 0 && !fetching && (
                <Button variant="outlined" startIcon={<Refresh />} onClick={() => lookupRows(refetchable)} sx={outlineBtnSx}>
                  Fetch details for {refetchable.length} row(s) in the list
                </Button>
              )}
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 2, p: 1.5, borderRadius: '10px', background: ownContent ? '#F1FAF3' : '#FFFAEB', border: `1px solid ${ownContent ? '#B7E1C1' : '#FEDF89'}`, flexWrap: 'wrap', gap: 1 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={ownContent}
                  onChange={(e) => toggleOwnContent(e.target.checked)}
                  sx={{ '& .Mui-checked': { color: PRIMARY }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY } }}
                />
                <Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1D1F2C' }}>Use our own content</Typography>
                  <Typography sx={{ fontSize: '12px', color: MUTED }}>
                    {ownContent
                      ? 'Only facts are taken from the supplier (part number, manufacturer, package, RoHS). Description is written from the facts and the image comes from our own website.'
                      : 'Supplier description, images, datasheet links and lead time are copied as they are.'}
                  </Typography>
                </Box>
              </Stack>
              {summary.found > 0 && (
                <Button size="small" variant="outlined" onClick={removeSupplierContent} disabled={fetching} sx={outlineBtnSx}>
                  Remove supplier content from fetched rows
                </Button>
              )}
            </Stack>
            {fetching && (
              <Box sx={{ mt: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress.total ? (progress.done / progress.total) * 100 : 0}
                  sx={{ height: 6, borderRadius: 3, background: PRIMARY_SOFT, '& .MuiLinearProgress-bar': { background: PRIMARY } }}
                />
                <Typography sx={{ fontSize: '12px', color: MUTED, mt: 0.5 }}>
                  {progress.done} / {progress.total} looked up
                </Typography>
              </Box>
            )}
        </Box>
      </Paper>

      {warnings.map((w) => (
        <Alert key={w} severity="warning" sx={{ mb: 1 }} onClose={() => setWarnings((list) => list.filter((x) => x !== w))}>
          {w}
        </Alert>
      ))}

      {/* Step 3 */}
      <Paper sx={{ ...cardSx, p: 0, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, pt: 2.25, pb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <SectionTitle sub="Check the fetched details, pick a category and add stock, price and GST.">Review & complete</SectionTitle>
          <Button color="error" startIcon={<DeleteOutline />} onClick={clearAll} disabled={!rows.length || fetching} sx={{ textTransform: 'none' }}>
            Clear list
          </Button>
        </Stack>
        {rows.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, px: 2.5, pb: 2 }}>
            <StatTile icon={<Inventory2 />} label="Parts in list" value={summary.total} tone="neutral" />
            <StatTile icon={<CloudDone />} label="Details fetched" value={summary.found} tone="success" />
            <StatTile
              icon={<WarningAmber />}
              label="Need attention"
              value={summary.notFound + summary.failed + summary.existing}
              hint={[summary.notFound && `${summary.notFound} not found`, summary.failed && `${summary.failed} failed`, summary.existing && `${summary.existing} in catalog`].filter(Boolean).join(' · ')}
              tone="warning"
            />
            <StatTile icon={<FactCheck />} label="Ready to upload" value={summary.ready} tone="primary" />
          </Box>
        )}

        {rows.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center', borderTop: `1px solid ${BORDER}` }}>
            <PlaylistAdd sx={{ fontSize: 40, color: '#C9BDEB' }} />
            <Typography sx={{ fontSize: '14px', color: MUTED, mt: 1 }}>
              Paste part numbers above. Description, manufacturer, datasheet, image, category and attributes are filled in automatically.
              <br />
              You only add stock, price and GST.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: '68vh', overflowY: 'auto', overflowX: 'hidden', borderTop: `1px solid ${BORDER}` }}>
            {/* Fixed column widths so the table always fits the page (no sideways scroll). */}
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead
                sx={{
                  '& .MuiTableCell-head': {
                    position: 'sticky',
                    top: 0,
                    zIndex: 3,
                    background: '#F9F9FC',
                    boxShadow: `inset 0 -1px 0 ${BORDER}`,
                  },
                }}
              >
                <TableRow>
                  <TableCell padding="none" sx={{ ...headCellSx, width: 56, px: 0, textAlign: 'center' }}>
                    <Checkbox
                      size="small"
                      checked={selectedRows.length === rows.length}
                      indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length}
                      onChange={(e) => setRows((prev) => prev.map((r) => ({ ...r, include: e.target.checked })))}
                      icon={<SelectionCheckboxIcon />}
                      checkedIcon={<SelectionCheckboxIcon state="checked" />}
                      indeterminateIcon={<SelectionCheckboxIcon state="indeterminate" />}
                      sx={{ p: 0.75, color: PRIMARY, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: PRIMARY } }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...headCellSx, width: '31%' }}>Product</TableCell>
                  <TableCell sx={{ ...headCellSx, width: '19%' }}>Category</TableCell>
                  <TableCell sx={{ ...headCellSx, width: '7%' }}>Stock</TableCell>
                  <TableCell sx={{ ...headCellSx, width: '8%' }}>Unit Price</TableCell>
                  <TableCell sx={{ ...headCellSx, width: '6%' }}>GST</TableCell>
                  <TableCell sx={{ ...headCellSx, width: '8%' }}>Sale Price</TableCell>
                  <TableCell sx={{ ...headCellSx, width: '8%' }}>Status</TableCell>
                  <TableCell sx={{ ...headCellSx, width: '8%' }}>Added</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 108, px: 0.5 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((row, index) => (
                  <QuickListRow
                    key={row.id}
                    row={row}
                    index={safePage * PAGE_SIZE + index}
                    categoryById={categoryById}
                    onChange={updateRow}
                    onRemove={removeRow}
                    onEdit={openEditor}
                    onView={openPreview}
                    showIssues={showIssues}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {pageCount > 1 && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: '12.5px', color: MUTED }}>
              Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, rows.length)} of {rows.length}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="outlined" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} sx={outlineBtnSx}>
                Previous
              </Button>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, px: 1 }}>
                {safePage + 1} / {pageCount}
              </Typography>
              <Button size="small" variant="outlined" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} sx={outlineBtnSx}>
                Next
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>

      {/* Sticky actions */}
      {rows.length > 0 && (
        <Paper
          sx={{
            position: 'sticky',
            bottom: 12,
            mt: 2,
            px: 2.5,
            py: 1.5,
            borderRadius: '10px',
            border: `1px solid ${BORDER}`,
            boxShadow: '0 6px 24px rgba(16,24,40,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            zIndex: 2,
          }}
        >
          <Typography sx={{ fontSize: '13px', color: MUTED }}>
            <b style={{ color: '#1D1F2C' }}>{selectedRows.length}</b> of {rows.length} selected · {summary.ready} ready
            {selectedInvalidCount > 0 && <span style={{ color: '#B54708', fontWeight: 600 }}> · {selectedInvalidCount} need review</span>}
          </Typography>
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={handleNext}
            disabled={!canMoveSelectedRows}
            title={selectedInvalidCount ? 'Fix the selected products marked Review before continuing' : undefined}
            sx={primaryBtnSx}
          >
            Next: All Quick Products ({selectedRows.length})
          </Button>
        </Paper>
      )}

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700, pb: 0.75 }}>
          {confirmAction?.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: MUTED, fontSize: '14px', lineHeight: 1.55 }}>
            {confirmAction?.description}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Button onClick={() => setConfirmAction(null)} sx={{ textTransform: 'none', color: MUTED, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              confirmAction?.onConfirm();
              setConfirmAction(null);
            }}
            sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
          >
            {confirmAction?.confirmLabel || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {editingRow && (
        <RowEditor
          key={editingRow.id}
          row={editingRow}
          focus={editing.focus}
          attributeOptions={attributeOptions}
          categoryOptions={categoryOptions}
          categoryById={categoryById}
          onClose={() => setEditing(null)}
          onSave={saveEditor}
        />
      )}

      {viewingRow && (() => {
        const previewCategory = categoryById.get(viewingRow.categoryId);
        const previewDescription = viewingRow.description || buildOwnDescription(viewingRow, categoryById);
        const previewPrice = parsePriceBreaks(viewingRow.prices, viewingRow.moq)[0];
        return (
          <Dialog open onClose={() => setViewingRowId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700 }}>Quick product details</DialogTitle>
            <DialogContent dividers>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {viewingRow.image ? (
                  <Box component="img" src={viewingRow.image} alt="" sx={{ width: 88, height: 88, objectFit: 'contain', border: `1px solid ${BORDER}`, borderRadius: '8px' }} />
                ) : (
                  <Box sx={{ width: 88, height: 88, borderRadius: '8px', background: PRIMARY_SOFT, display: 'grid', placeItems: 'center' }}><Memory sx={{ color: '#B7A6E6' }} /></Box>
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '18px', wordBreak: 'break-word' }}>{viewingRow.productNumber || viewingRow.partNumber}</Typography>
                  <Typography sx={{ color: MUTED, mt: 0.5 }}>{viewingRow.manufacturer || 'Manufacturer not available'}</Typography>
                  <Box sx={{ mt: 0.75 }}><StatusChip row={viewingRow} /></Box>
                </Box>
              </Stack>
              <Typography sx={{ color: '#344054', fontSize: '14px', lineHeight: 1.55, mb: 2 }}>{previewDescription || 'No description available.'}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
                <Box><Typography sx={{ fontSize: '12px', color: MUTED }}>Category</Typography><Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{previewCategory?.label || viewingRow.supplierCategory || '—'}</Typography></Box>
                <Box><Typography sx={{ fontSize: '12px', color: MUTED }}>Available stock</Typography><Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{viewingRow.stock || 0}</Typography></Box>
                <Box><Typography sx={{ fontSize: '12px', color: MUTED }}>Unit price</Typography><Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{previewPrice ? `₹${previewPrice.unitPrice}` : '—'}</Typography></Box>
                <Box><Typography sx={{ fontSize: '12px', color: MUTED }}>GST</Typography><Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{viewingRow.gst || 0}%</Typography></Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setViewingRowId(null)} sx={{ textTransform: 'none', color: MUTED }}>Close</Button>
              <Button variant="contained" onClick={() => { setViewingRowId(null); openEditor(viewingRow.id); }} sx={primaryBtnSx}>Edit product</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {connectOpen && (
        <ConnectDialog
          providers={providers || null}
          browserMode={browserMode}
          browserKey={browserKey}
          onBrowserKeySaved={setBrowserKey}
          onClose={() => setConnectOpen(false)}
          onSaved={(next) => {
            if (next) setProviders(next);
            else loadProviders();
          }}
        />
      )}
    </Box>
  );
};

export default QuickList;
